import { createSlice } from '@reduxjs/toolkit';

// Initialize state from localStorage (only UI state, not VTO data)
const loadPersistedState = () => {
  try {
    const savedState = localStorage.getItem('vtoUIState');
    if (savedState) {
      const parsed = JSON.parse(savedState);
      // Validate the structure and only load UI state
      if (parsed && typeof parsed === 'object') {
        return {
          selectedVTOResultId: parsed.selectedVTOResultId || null,
          currentSessionId: parsed.currentSessionId || null,
          currentSession: parsed.currentSession || null,
          isVTOMode: parsed.isVTOMode || false,
          showHistory: parsed.showHistory || false,
          historyFilters: parsed.historyFilters || {
            garmentType: 'all',
            favorites: false,
            sortBy: 'createdAt:desc',
            limit: 20
          },
          settings: parsed.settings || {
            autoSaveResults: true,
            showProcessingDetails: true,
            maxRetries: 3,
            processingTimeout: 120000,
            qualityPreference: 'balanced'
          },
          // Don't persist loading/error states
          isProcessing: false,
          error: null,
          progress: 0
        };
      }
    }
  } catch (error) {
    console.warn('Failed to load persisted VTO UI state:', error);
    // Clear corrupted state
    try {
      localStorage.removeItem('vtoUIState');
    } catch (clearError) {
      console.warn('Failed to clear corrupted state:', clearError);
    }
  }
  return null;
};

const persistedState = loadPersistedState();

const defaultState = {
  // Current VTO session (UI state only)
  currentSession: {
    personImageId: null, // Reference to person image from RTK Query
    selectedClothingIds: [], // References to clothing items from RTK Query
    resultImageId: null, // Reference to result image from RTK Query
    vtoResultId: null, // Reference to saved VTO result from RTK Query
    isProcessing: false,
    processingStage: null, // 'uploading' | 'processing' | 'generating' | 'complete'
    progress: 0,
    error: null,
    sessionId: null,
    startTime: null,
    endTime: null,
    metadata: {
      garmentType: null,
      processingTime: null,
      apiCost: null,
      confidence: null,
      garmentFit: 'good'
    }
  },

  // UI State
  isVTOMode: false,
  showHistory: false,
  selectedVTOResultId: null, // Reference to VTO result from RTK Query

  // UI filters for history
  historyFilters: {
    garmentType: 'all',
    favorites: false,
    sortBy: 'createdAt:desc',
    limit: 20
  },

  // Settings
  settings: {
    autoSaveResults: true,
    showProcessingDetails: true,
    maxRetries: 3,
    processingTimeout: 120000, // 2 minutes
    qualityPreference: 'balanced' // 'fast' | 'balanced' | 'quality'
  }
};

// Merge persisted state with default state to ensure all properties exist
const initialState = persistedState ? {
  ...defaultState,
  ...persistedState,
  // Ensure nested objects are properly merged
  currentSession: {
    ...defaultState.currentSession,
    ...(persistedState.currentSession || {})
  },
  historyFilters: {
    ...defaultState.historyFilters,
    ...(persistedState.historyFilters || {}),
    limit: defaultState.historyFilters.limit // Always use latest default limit
  },
  settings: {
    ...defaultState.settings,
    ...(persistedState.settings || {})
  }
} : defaultState;

const vtoSlice = createSlice({
  name: 'vto',
  initialState,
  reducers: {
    // Session Management
    startVTOSession: (state, action) => {
      const { personImageId, clothingIds } = action.payload;

      state.currentSession = {
        ...initialState.currentSession,
        personImageId,
        selectedClothingIds: Array.isArray(clothingIds) ? clothingIds : [clothingIds],
        sessionId: `vto_${Date.now()}`,
        startTime: Date.now(),
        isProcessing: true,
        processingStage: 'uploading',
        progress: 0
      };

      state.isVTOMode = true;
    },

    updateVTOSession: (state, action) => {
      state.currentSession = {
        ...state.currentSession,
        ...action.payload
      };
    },

    // Sync person image ID with person image slice
    syncPersonImageId: (state, action) => {
      const { personImageId } = action.payload;
      if (state.currentSession.personImageId !== personImageId) {
        state.currentSession.personImageId = personImageId;
      }
    },

    setVTOResult: (state, action) => {
      const { resultImageId, vtoResultId, metadata = {}, personImageId } = action.payload;

      state.currentSession.resultImageId = resultImageId;
      state.currentSession.vtoResultId = vtoResultId; // Reference to saved VTO result
      if (personImageId) {
        state.currentSession.personImageId = personImageId;
      }
      state.currentSession.metadata = {
        ...state.currentSession.metadata,
        ...metadata
      };
      state.currentSession.isProcessing = false;
      state.currentSession.processingStage = 'complete';
      state.currentSession.endTime = Date.now();
      state.currentSession.progress = 100;
      state.currentSession.error = null;
    },

    setVTOError: (state, action) => {
      const { error, stage } = action.payload;

      state.currentSession.error = error;
      state.currentSession.isProcessing = false;
      state.currentSession.processingStage = stage || 'error';
      state.currentSession.endTime = Date.now();
    },

    clearVTOSession: (state) => {
      state.currentSession = { ...initialState.currentSession };
      state.isVTOMode = false;
    },

    setVTOMode: (state, action) => {
      state.isVTOMode = action.payload;
      if (!action.payload) {
        // Clear session when exiting VTO mode
        state.currentSession = { ...initialState.currentSession };
      }
    },

    // History UI Management (data comes from RTK Query)
    selectVTOResult: (state, action) => {
      const resultId = action.payload;
      state.selectedVTOResultId = resultId;
    },

    clearSelectedVTOResult: (state) => {
      state.selectedVTOResultId = null;
    },

    // History filters
    setHistoryFilters: (state, action) => {
      state.historyFilters = { ...state.historyFilters, ...action.payload };
    },

    setHistoryGarmentTypeFilter: (state, action) => {
      state.historyFilters.garmentType = action.payload;
    },

    setHistoryFavoritesFilter: (state, action) => {
      state.historyFilters.favorites = action.payload;
    },

    setHistorySortBy: (state, action) => {
      state.historyFilters.sortBy = action.payload;
    },

    // UI State
    setShowHistory: (state, action) => {
      state.showHistory = action.payload;
    },

    // Settings
    updateVTOSettings: (state, action) => {
      state.settings = {
        ...state.settings,
        ...action.payload
      };
    },

    // Processing State
    setProcessingStage: (state, action) => {
      state.currentSession.processingStage = action.payload;
    },

    setProcessingProgress: (state, action) => {
      state.currentSession.progress = Math.max(0, Math.min(100, action.payload));
    },

    // Reset entire state
    resetVTOState: (state) => {
      Object.assign(state, initialState);
    },

    // Legacy action for backward compatibility (deprecated)
    loadHistoryFromStorage: (state, action) => {
      console.warn('loadHistoryFromStorage is deprecated. History is now loaded from backend via RTK Query.');
      // No-op for backward compatibility - history is now handled by RTK Query
    }
  }
});

export const {
  startVTOSession,
  updateVTOSession,
  syncPersonImageId,
  setVTOResult,
  setVTOError,
  clearVTOSession,
  setVTOMode,
  selectVTOResult,
  clearSelectedVTOResult,
  setHistoryFilters,
  setHistoryGarmentTypeFilter,
  setHistoryFavoritesFilter,
  setHistorySortBy,
  setShowHistory,
  updateVTOSettings,
  setProcessingStage,
  setProcessingProgress,
  resetVTOState,
  loadHistoryFromStorage
} = vtoSlice.actions;

// Selectors for UI state only (data comes from RTK Query)
export const selectCurrentVTOSession = (state) => state.vto.currentSession;
export const selectVTOSettings = (state) => state.vto.settings;
export const selectIsVTOMode = (state) => state.vto.isVTOMode;
export const selectShowHistory = (state) => state.vto.showHistory;
export const selectSelectedVTOResultId = (state) => state.vto.selectedVTOResultId;
export const selectHistoryFilters = (state) => state.vto.historyFilters;
export const selectIsVTOProcessing = (state) => state.vto.currentSession.isProcessing;
export const selectVTOProgress = (state) => state.vto.currentSession.progress;
export const selectVTOError = (state) => state.vto.currentSession.error;
export const selectVTOResultImageId = (state) => state.vto.currentSession.resultImageId;
export const selectVTOResultId = (state) => state.vto.currentSession.vtoResultId;
export const selectProcessingStage = (state) => state.vto.currentSession.processingStage;

// Session selectors
export const selectCurrentPersonImageId = (state) => state.vto.currentSession.personImageId;
export const selectCurrentClothingIds = (state) => state.vto.currentSession.selectedClothingIds;
export const selectCurrentSessionId = (state) => state.vto.currentSession.sessionId;
export const selectCurrentSessionMetadata = (state) => state.vto.currentSession.metadata;

// Computed selectors
export const selectHasVTOResult = (state) => !!state.vto.currentSession.resultImageId;
export const selectVTOSessionDuration = (state) => {
  const session = state.vto.currentSession;
  if (!session.startTime) return 0;
  const endTime = session.endTime || Date.now();
  return endTime - session.startTime;
};

export const selectHasActiveVTOSession = (state) => {
  const session = state.vto.currentSession;
  return !!(session.personImageId && session.selectedClothingIds.length > 0);
};

// Filter selectors
export const selectHistoryGarmentTypeFilter = (state) => state.vto.historyFilters.garmentType;
export const selectHistoryFavoritesFilter = (state) => state.vto.historyFilters.favorites;
export const selectHistorySortBy = (state) => state.vto.historyFilters.sortBy;

export default vtoSlice.reducer;

// Persistence middleware (only for UI state, not VTO data)
export const persistVTOState = (store) => (next) => (action) => {
  const result = next(action);

  // Only persist on VTO actions
  if (action.type.startsWith('vto/')) {
    const state = store.getState().vto;

    // Create a clean UI state object for persistence (include vtoResultId for restoration)
    const persistState = {
      selectedVTOResultId: state.selectedVTOResultId,
      currentSessionId: state.currentSession.sessionId,
      currentSession: {
        vtoResultId: state.currentSession.vtoResultId,
        personImageId: state.currentSession.personImageId
      },
      isVTOMode: state.isVTOMode,
      showHistory: state.showHistory,
      historyFilters: state.historyFilters,
      settings: state.settings
    };

    try {
      localStorage.setItem('vtoUIState', JSON.stringify(persistState));
    } catch (error) {
      console.warn('Failed to persist VTO UI state:', error);

      // If quota exceeded, try to clear some space
      if (error.name === 'QuotaExceededError') {
        try {
          // Clear old state and try again
          localStorage.removeItem('vtoUIState');
          localStorage.setItem('vtoUIState', JSON.stringify(persistState));
        } catch (retryError) {
          console.error('Still failed to persist after clearing space:', retryError);
        }
      }
    }
  }

  return result;
};